// @flow
import {combineReducers} from 'redux';
import {combineEpics} from 'redux-observable';

import type {Action} from '../store/utils';
import type {AuthState} from './auth/duck';
import type {CategoryState} from './categories/duck';
import type {CountryState} from './country/duck';
import type {CurrencyState} from './currency/duck';
import type {ProductsState} from './products/duck';
import type {OrdersState} from './orders/duck';
import type {OrderState} from './order/duck';
import type {ManualOrderState} from './manual-order/duck';
import type {OrganizeProductsState} from './organize-products/duck';
import type {ReferralState} from './referrals/duck';
import type {CrmState} from './crm/duck';
import type {SubcategoryState} from './sub-categories/duck';
import type {AccountConfigState} from './profile/account/duck';
import type {SocialMediasConfigState} from './configure/social-medias/duck';
import type {PaymentMethodConfigState} from './configure/payment-method/duck';
import type {GeneralConfigState} from './configure/general/duck';
// import type {LogisticsConfigState} from './configure/logistics/duck';
import type {LegalConfigState} from './configure/legal/duck';
import type {TaxesConfigState} from './configure/taxes/duck';
import type {AccountsConfigState} from './configure/accounts/duck';
import type {PolygonsState} from './map/duck';
import type {SalesProcessConfigState} from './configure/sales-process/duck';
import type {StoreState} from './store/duck';
import type {InformationState} from './information/duck';
import type {UiState} from './ui/duck';
import type {OnBoardingState} from './on-boarding/duck';
import type {CheckoutState} from './checkout/duck';
import type {TiersListState} from './tiers/duck';
import type {DashboardState} from './dashboard/duck';
import type {ReviewsState} from './marketing/reviews/duck';
import type {CouponsState} from './marketing/coupons/duck';
import type {PromotionsState} from './marketing/promotions/duck';
import type {ReportsState} from './marketing/reports/duck';
import type {NotificationsState} from './store-notifications/duck';
import type {MyStoreState} from './my-store/customize/duck';
import type {CustomDomainState} from './customDomain/duck';
import type {ProductPageState} from './my-store/product-page/duck';
import type {BulkEditState} from './bulk-edit/duck';
import type {MultiChatState} from './multichat/chat/duck';
import type {ChatTriggerState} from './multichat/chat-triggers/duck';
import type {MultichatFunnelState} from './multichat/funnels/duck';
import type {QuickReplyState} from './multichat/quick-replies/duck';
import type {HomePageState} from './home-pages/duck';
import type {ProductVisibilityState} from './product-visibility/duck';
import type {EInvoiceState} from './tax-invoice/duck';
import type {RealTimeMultichatState} from './multichat/real-time/duck';
import type {ScheduleMessageState} from './multichat/schedule-messages/duck';
import type {BlogState} from './blog/duck';
import type {TierRestrictionState} from './TierRestriction/duck';
import type {ToastAlertState} from './toast-alert/duck';
import type {ProductFiltersState} from './product-filters/duck';
import type {PageLayoutState} from './page-layout/duck';
import type {ProductBatchUploadState} from './product-batch-load/duck';

import type {DeliveryMethodsConfigState} from './configure/logistics/logistics/duck';
import type {PickupConfigState} from './configure/logistics/logistic-pickup/duck';
import type {ShippingConfigState} from './configure/logistics/logistic-shipping/duck';
import type {PromotionConfigState} from './configure/logistics/logistic-promotions/duck';

import authReducer, {
  initialState as AuthInitialState,
  Logout,
  getSessionEpic,
  refreshSessionEpic,
  loginEpic,
  retryLoginEpic,
  sendEmailRecoveryEpic,
  resetPasswordEpic,
  getDomainChangeTokenEpic,
  // registerEpic,
  logoutEpic,
} from './auth/duck';
import categoryReducer, {
  initialState as CategoryInitialState,
  fetchCategoriesEpic,
  fetchCategoriesSearchEpic,
  createCategoryEpic,
  editCategoryEpic,
  deleteCategoryEpic,
  sortCategoriesEpic,
} from './categories/duck';
import countryReducer, {
  initialState as CountryInitialState,
  fetchCountriesEpic,
  getStoreCountryEpic,
  saveStoreCountryEpic,
} from './country/duck';
import CurrencyReducer, {
  initialState as CurrencyInitialState,
  fetchCurrencyEpic,
  fetchCurrenciesEpic,
  setCurrencyEpic,
  fetchGeolocationEpic,
} from './currency/duck';
import productReducer, {
  initialState as ProductsInitialState,
  fetchProductEpic,
  fetchProductsEpic,
  fetchFeaturedProductsEpic,
  createProductEpic,
  saveProductSectionEpic,
  editProductSectionEpic,
  addProductImageEpic,
  enableProductEpic,
  editProductEpic,
  deleteProductEpic,
  saveProductVariantsEpic,
  editProductVariantEpic,
  exportProductsEpic,
  batchUpdateProductsEpic,
  batchCreateProductsEpic,
  fetchFbFeedTokenEpic,
  fetchProductsCatalogueEpic,
  fetchProductVariantOptionsEpic,
  addProductsCatalogueEpic,
  fetchProductMetadataSettingEpic,
  fetchProductSizeChartsEpic,
  fetchProductSpecificationsEpic,
  fetchRelatedProductsEpic,
} from './products/duck';
import organizeProductsReducer, {
  initialState as OrganizeProductsInitialState,
  fetchOrganizedProductsEpic,
  sortProductEpic,
} from './organize-products/duck';
import ordersReducer, {
  initialState as OrdersInitialState,
  fetchOrdersEpic,
  fetchPreOrdersEpic,
  fetchOrdersCountEpic,
  fetchCollaboratorInfoEpic,
  fetchOrdersTaxInvoiceDataEpic,
} from './orders/duck';
import orderReducer, {
  initialState as OrderInitialState,
  fetchOrderEpic,
  fetchOrderStockErrorsEpic,
  fetchOrderTaxInvoiceDataEpic,
  updateOrderStateEpic,
  updateOrderCollaboratorEpic,
  fetchPreOrderEpic,
  updatePreOrderStateEpic,
  // fetchOrderAvailableRevertStatesEpic,
  // deleteOrderEpic,
  saveOrderCommentEpic,
  fetchOrderCommentsEpic,
  updateOrderCommentEpic,
  deleteOrderCommentEpic,
} from './order/duck';
import manualOrderReducer, {
  initialState as ManualOrderInitialState,
  fetchManualOrderProductsEpic,
  fetchShoppingCartPricesEpic,
  fetchProductPriceEpic,
  fetchOrderSummaryEpic,
  createOrderEpic,
  editOrderEpic,
  createPreOrderEpic,
  editPreOrderEpic,
  searchCustomersEpic,
} from './manual-order/duck';
import accountConfigReducer, {
  initialState as AccountConfigInitialState,
  saveAccountEpic,
  fetchSubscriptionSettingEpic,
  changeAccountPasswordEpic,
  saveSubscriptionSettingEpic,
} from './profile/account/duck';
import socialMediasConfigReducer, {
  initialState as SocialMediasConfigInitialState,
  saveSocialMediasEpic,
} from './configure/social-medias/duck';
import PaymentMethodConfigReducer, {
  initialState as PaymentMethodConfigInitialState,
  getPaymentMethodsEpic,
  addPaymentMethodEpic,
  updatePaymentMethodEpic,
  deletePaymentMethodEpic,
  getPaymentMethodEpic,
  updateMercadoPagoCheckoutProEpic,
  updateIziPayEpic,
  addLogoPaymentMethodEpic,
  updateCulqiEpic,
  updateStripeEpic,
  updatePaypalEpic,
  updateTransbankEpic,
  updateNiubizPayLinkEpic,
  updateNiubizWebEpic,
} from './configure/payment-method/duck';
import generalConfigReducer, {
  initialState as GeneralConfigInitialState,
  saveGeneralSettingsEpic,
  saveGeneralInformationEpic,
  saveStoreProfileInformationEpic,
  saveGeneralContactEpic,
} from './configure/general/duck';
// import logisticsConfigReducer, {
//   initialState as LogisticsConfigInitialState,
//   // getShippingMethodEpic,
//   // fetchOrderShippingMethodEpic,
//   // getPickupMethodEpic,
//   // saveShippingMethodEpic,
//   // savePickupMethodEpic,
//   // fetchFlatRateEpic,
//   // saveFlatRateEpic,
//   // editFlatRateEpic,
//   // deleteFlatRateEpic,
//   // saveAdminRateEpic,
//   // deleteAdminRateEpic,
//   // fetchAdminRateEpic,
//   // editAdminRateEpic,
//   // savePromotionEpic,
// } from './configure/logistics/duck';

import logisticsDeliveryMethodsConfigReducer, {
  initialState as LogisticsDeliveryMethodsConfigInitialState,
  getLogisticDeliveryMethodsEpic,
} from './configure/logistics/logistics/duck';
import logisticsShippingConfigReducer, {
  initialState as LogisticsShippingConfigInitialState,
  getLogisticShippingMethodEpic,
  updateEnabledShippingMethodsEpic,
  saveCountriesEpic,
  saveActiveTypeRateEpic,
  fetchFlatRateEpic,
  saveFlatRateEpic,
  editFlatRateEpic,
  deleteFlatRateEpic,
  saveShippingAdvancedEpic,
  fetchShippingAdvancedEpic,
  editShippingAdvancedEpic,
  deleteShippingAdvancedEpic,
  saveShippingStandardEpic,
  fetchShippingStandardEpic,
  editShippingStandardEpic,
  deleteShippingStandardEpic,
  updateAllowOrderOutsideDeliveryZoneEpic,
} from './configure/logistics/logistic-shipping/duck';
import logisticsPromotionConfigReducer, {
  initialState as LogisticsPromotionConfigInitialState,
  getLogisticPromotionEpic,
  saveLogisticPromotionEpic,
  editLogisticPromotionEpic,
  deleteLogisticPromotionEpic,
} from './configure/logistics/logistic-promotions/duck';
import logisticsPickupConfigReducer, {
  initialState as LogisticsPickupConfigInitialState,
  getLogisticPickupRateEpic,
  getLogisticPickupMethodEpic,
  logisticSavePickupRateEpic,
  logisitcDeletePickupRateEpic,
  enabledPickupMethodEpic,
  logisticUpdatePickupRateEpic,
} from './configure/logistics/logistic-pickup/duck';

import taxesConfigReducer, {
  initialState as TaxesConfigInitialState,
  fetchTaxesSettingEpic,
  createTaxEpic,
  fetchTaxesEpic,
  fetchTaxEpic,
  fetchProductTaxesEpic,
  updateTaxEpic,
} from './configure/taxes/duck';
import crmReducer, {
  initialState as CrmInitialState,
  fetchCustomersEpic,
  fetchCustomerEpic,
  editCustomerEpic,
  fetchCustomerOrdersEpic,
  fetchCustomerCategoriesEpic,
  createCustomerCategoryEpic,
  editCustomerCategoryEpic,
  deleteCustomerCategoryEpic,
  getCustomerMessageEpic,
  saveCustomerMessageEpic,
} from './crm/duck';
import legalConfigReducer, {
  initialState as LegalConfigInitialState,
  fetchStoreLegalSettingEpic,
  saveStoreLegalSettingEpic,
  createFaqEpic,
  fetchFaqEpic,
  deleteFaqEpic,
  editFaqEpic,
  fetchTermsEpic,
  createTermsEpic,
  fetchRefundsEpic,
  createRefundsEpic,
  fetchShippingsEpic,
  createShippingsEpic,
  fetchAboutUsConfigEpic,
  createAboutUsConfigEpic,
} from './configure/legal/duck';
import accountsConfigReducer, {
  initialState as AccountsConfigInitialState,
  getCollaboratorsEpic,
  getCollaboratorEpic,
  createCollaboratorEpic,
  updateCollaboratorEpic,
} from './configure/accounts/duck';
import polygonReducer, {
  initialState as PolygonInitialState,
  // fetchGeometryEpic,
} from './map/duck';
import referralReducer, {
  initialState as ReferralInitialState,
  fetchStorePointEpic,
  fetchStoreReferredsEpic,
  fetchStoreReferredsInfoEpic,
  redeemStorePointsEpic,
  getRedeemedBenefitEpic,
} from './referrals/duck';
import subcategoriesReducer, {
  initialState as SubcategoriesInitialState,
  fetchSubcategoriesEpic,
  createSubcategoryEpic,
  updateSubcategoryEpic,
  deleteSubcategoryEpic,
  sortSubcategoryEpic,
} from './sub-categories/duck';
import salesProcessConfigReducer, {
  initialState as SalesProcessConfigInitialState,
  getSalesProcessInformationEpic,
  getOrderStateMessagesEpic,
  updateOrderStateMessagesEpic,
  createCheckoutInputEpic,
  editCheckoutInputEpic,
  deleteCheckoutInputEpic,
  sortCheckoutInputEpic,
  getCheckoutInputEpic,
} from './configure/sales-process/duck';
import storeReducer, {
  initialState as StoreInitialState,
  fetchStoreEpic,
  fetchStorePreviousTierEpic,
  fetchStoreMetadataEpic,
} from './store/duck';
import informationReducer, {
  initialState as InformationInitialState,
  fetchContactTypesEpic,
  fetchSocialMediaTypesEpic,
  fetchContactEpic,
  fetchSocialEpic,
} from './information/duck';
import uiReducer, {
  initialState as UiInitialState,
  fetchStoreInformationEpic,
  fetchStoreProfileInformationEpic,
  fetchResellerInformationEpic,
  saveFacebookIntegrationEpic,
  fetchFacebookIntegrationEpic,
  saveDowngradeInformationEpic,
  getStoreProductSettingEpic,
  saveStoreProductSettingEpic,
} from './ui/duck';
import onBoardingReducer, {
  initialState as OnBoardingInitialState,
  setInitialConfigEpic,
  saveCanalInfEpic,
  validateDomainEpic,
  getStoreTypesEpic,
  saveOnboardingInfEpic,
  getSupportInformationEpic,
  getGoalInformationEpic,
} from './on-boarding/duck';
import checkoutReducer, {
  initialState as CheckoutInitialState,
  fetchPaymentProvidersEpic,
  deleteCardEpic,
  fetchCardsEpic,
  fetchCardEpic,
  fetchPurchaseSummaryEpic,
  performSubscribeEpic,
  freeTrialSubscribeEpic,
  payUInitializeEpic,
  stripeInitializeEpic,
  stripeUpdateEpic,
  stripeAddCardInitializeEpic,
  checkCardStateEpic,
  setCurrentCardEpic,
} from './checkout/duck';

import tiersListReducer, {
  initialState as TiersListInitialState,
  fetchSubscriptionInformationEpic,
  fetchSubscriptionPriceEpic,
} from './tiers/duck';

import dashboardReducer, {
  initialState as DashboardInitialState,
  fetchProductsCountEpic,
  fetchDataAnalyticsEpic,
  fetchPageViewsEpic,
  fetchSummaryProductsEpic,
  fetchSummaryOrdersEpic,
  fetchSummaryProductCategoriesEpic,
  fetchSummarySalesEpic,
  fetchSummaryOrderCustomersEpic,
  fetchSummaryLessStockProductsEpic,
  fetchFlagLessStockProductsEpic,
  fetchSummaryPromotionSalesEpic,
  fetchSummaryPageViewsEpic,
  completeFirstStepsEpic,
  domainExpositionEpic,
  fetchCompleteFirstStepsEpic,
  fetchDomainExpositionEpic,
  saveCompletedConfigEpic,
  fetchCompletedConfigEpic,
  fetchSummaryOrderAmountEpic,
  fetchSummaryAverageTicketEpic,
} from './dashboard/duck';

import reviewsReducer, {
  initialState as ReviewsInitialState,
  fetchPendingReviewsEpic,
  fetchReviewsEpic,
  enableReviewEpic,
  fetchAverageEpic,
  sendEmailEpic,
} from './marketing/reviews/duck';

import couponsReducer, {
  initialState as CouponsInitialState,
  fetchCouponsEpic,
  fetchCouponEpic,
  createCouponEpic,
  updateCouponEpic,
} from './marketing/coupons/duck';

import promotionsReducer, {
  initialState as PromotionsInitialState,
  fetchPromotionsEpic,
  fetchPromotionEpic,
  updatePromotionEpic,
  createPromotionEpic,
  sortPromotionEpic,
} from './marketing/promotions/duck';

import reportsReducer, {
  initialState as ReportsInitialState,
  fetchGAIntegrationEpic,
  updateGAIntegrationEpic,
} from './marketing/reports/duck';

import notificationsReducer, {
  initialState as NotificationsInitialState,
  fetchNotificationsEpic,
  fetchUnseenNotificationsEpic,
  seenNotificationEpic,
  seenAllNotificationsEpic,
} from './store-notifications/duck';

import myStoreReducer, {
  initialState as MyStoreInitialState,
  saveStoreInformationEpic,
} from './my-store/customize/duck';

import productPageReducer, {
  initialState as ProdcutPageInitialState,
  saveSizeChartEpic,
  editSizeChartEpic,
  deleteSizeChartEpic,
  getProductPageSettingEpic,
  saveProductPageSettingEpic,
} from './my-store/product-page/duck';

import customDomainReducer, {
  initialState as CustomDomainInitialState,
  generateSslEpic,
  validateSslEpic,
  getCustomDomainEpic,
  deleteCustomDomainEpic,
} from './customDomain/duck';

import bulkEditReducer, {
  initialState as BulkEditInitialState,
  fetchBulkEditProductsEpic,
  editProductFieldEpic,
  editProductFieldBulkEpic,
  changeProductImageEpic,
} from './bulk-edit/duck';

import chatReducer, {
  initialState as ChatInitialState,
  fetchStoreDevicesEpic,
  getRavenAccountEpic,
} from './multichat/chat/duck';

import chatTriggerReducer, {
  initialState as ChatTriggerInitialState,
  // fetchTriggersEpic,
} from './multichat/chat-triggers/duck';
import multichatFunnelReducer, {
  initialState as MultichatFunnelInitialState,
} from './multichat/funnels/duck';
import homePageReducer, {
  initialState as HomePageInitialState,
  getBannersEpic,
  getStoreInformationBlocksEpic,
} from './home-pages/duck';

import quickReplyReducer, {
  initialState as QuickReplyInitialState,
} from './multichat/quick-replies/duck';
import productVisibilityReducer, {
  initialState as ProductVisibilityInitialState,
} from './product-visibility/duck';
import realTimeMultichatReducer, {
  initialState as RealTimeMultichatInitialState,
} from './multichat/real-time/duck';
import scheduleMessageReducer, {
  initialState as scheduleMessageInitialState,
} from './multichat/schedule-messages/duck';

import eInvoiceReducer, {
  initialState as EInvoiceInitialState,
} from './tax-invoice/duck';
import blogReducer, {initialState as BlogInitialState} from './blog/duck';
import tierRestrictionReducer, {
  initialState as TierRestrictionInitialState,
} from './TierRestriction/duck';

import toastAlertReducer, {
  initialState as ToastAlertInitialState,
} from './toast-alert/duck';

import productFiltersReducer, {
  initialState as ProductFiltersInitialState,
  fetchProductFiltersSettingEpic,
  saveProductFiltersSettingEpic,
  fetchProductSearchSettingEpic,
  saveProductSearchSettingEpic,
  fetchVectorizeStatusEpic,
} from './product-filters/duck';

import pageLayoutReducer, {
  initialState as PageLayoutInitialState,
  fetchPageLayoutEpic,
} from './page-layout/duck';

import productBatchLoadReducer, {
  initialState as ProductBatchLoadInitialState,
} from './product-batch-load/duck';
export type RootState = {
  auth: AuthState,
  categories: CategoryState,
  country: CountryState,
  currency: CurrencyState,
  products: ProductsState,
  orders: OrdersState,
  order: OrderState,
  referral: ReferralState,
  crm: CrmState,
  subCategories: SubcategoryState,
  manualOrder: ManualOrderState,
  organizeProducts: OrganizeProductsState,
  accountConfig: AccountConfigState,
  socialMediasConfig: SocialMediasConfigState,
  paymentMethodConfig: PaymentMethodConfigState,
  generalConfig: GeneralConfigState,
  map: PolygonsState,

  // // logisticsConfig: LogisticsConfigState,
  logisticsConfig: DeliveryMethodsConfigState,
  // logisticsDeliveryMethodsConfig: DeliveryMethodsConfigState,
  logisticShippingConfig: ShippingConfigState,
  logisticPickupConfig: PickupConfigState,
  logisticPromotionConfig: PromotionConfigState,

  legalConfig: LegalConfigState,
  taxesConfig: TaxesConfigState,
  accountsConfig: AccountsConfigState,
  salesProcessConfig: SalesProcessConfigState,
  store: StoreState,
  information: InformationState,
  ui: UiState,
  onBoarding: OnBoardingState,
  checkout: CheckoutState,
  tiersList: TiersListState,
  dashboard: DashboardState,
  reviews: ReviewsState,
  coupons: CouponsState,
  promotions: PromotionsState,
  reports: ReportsState,
  notifications: NotificationsState,
  myStore: MyStoreState,
  productPage: ProductPageState,
  customDomain: CustomDomainState,
  bulkEdit: BulkEditState,
  chat: MultiChatState,
  chatTriggers: ChatTriggerState,
  multichatFunnel: MultichatFunnelState,
  quickReply: QuickReplyState,
  homePage: HomePageState,
  scheduleMessage: ScheduleMessageState,
  productVisibility: ProductVisibilityState,
  taxInvoice: EInvoiceState,
  realTimeMultichat: RealTimeMultichatState,
  blog: BlogState,
  tierRestriction: TierRestrictionState,
  toastAlert: ToastAlertState,
  productFilters: ProductFiltersState,
  pageLayout: PageLayoutState,
  productBatchLoad: ProductBatchUploadState,
};

export const defaultInitialState = {
  auth: AuthInitialState,
  categories: CategoryInitialState,
  country: CountryInitialState,
  currency: CurrencyInitialState,
  products: ProductsInitialState,
  orders: OrdersInitialState,
  order: OrderInitialState,
  manualOrder: ManualOrderInitialState,
  organizeProducts: OrganizeProductsInitialState,
  accountConfig: AccountConfigInitialState,
  socialMediasConfig: SocialMediasConfigInitialState,
  paymentMethodConfig: PaymentMethodConfigInitialState,
  generalConfig: GeneralConfigInitialState,

  // logisticsConfig: LogisticsConfigInitialState,
  logisticsConfig: LogisticsDeliveryMethodsConfigInitialState,
  // logisticsDeliveryMethodsConfig: LogisticsDeliveryMethodsConfigInitialState,
  logisticShippingConfig: LogisticsShippingConfigInitialState,
  logisticPickupConfig: LogisticsPickupConfigInitialState,
  logisticPromotionConfig: LogisticsPromotionConfigInitialState,

  legalConfig: LegalConfigInitialState,
  taxesConfig: TaxesConfigInitialState,
  accountsConfig: AccountsConfigInitialState,
  map: PolygonInitialState,
  referral: ReferralInitialState,
  crm: CrmInitialState,
  subCategories: SubcategoriesInitialState,
  salesProcessConfig: SalesProcessConfigInitialState,
  store: StoreInitialState,
  information: InformationInitialState,
  ui: UiInitialState,
  onBoarding: OnBoardingInitialState,
  checkout: CheckoutInitialState,
  tiersList: TiersListInitialState,
  dashboard: DashboardInitialState,
  reviews: ReviewsInitialState,
  coupons: CouponsInitialState,
  promotions: PromotionsInitialState,
  reports: ReportsInitialState,
  notifications: NotificationsInitialState,
  myStore: MyStoreInitialState,
  productPage: ProdcutPageInitialState,
  customDomain: CustomDomainInitialState,
  bulkEdit: BulkEditInitialState,
  chat: ChatInitialState,
  chatTriggers: ChatTriggerInitialState,
  multichatFunnel: MultichatFunnelInitialState,
  quickReply: QuickReplyInitialState,
  scheduleMessage: scheduleMessageInitialState,
  homePage: HomePageInitialState,
  productVisibility: ProductVisibilityInitialState,
  taxInvoice: EInvoiceInitialState,
  realTimeMultichat: RealTimeMultichatInitialState,
  blog: BlogInitialState,
  tierRestriction: TierRestrictionInitialState,
  toastAlert: ToastAlertInitialState,
  productFilters: ProductFiltersInitialState,
  pageLayout: PageLayoutInitialState,
  productBatchLoad: ProductBatchLoadInitialState,
};

export const appReducer = combineReducers({
  auth: authReducer,
  categories: categoryReducer,
  country: countryReducer,
  currency: CurrencyReducer,
  products: productReducer,
  organizeProducts: organizeProductsReducer,
  orders: ordersReducer,
  order: orderReducer,
  manualOrder: manualOrderReducer,
  accountConfig: accountConfigReducer,
  socialMediasConfig: socialMediasConfigReducer,
  paymentMethodConfig: PaymentMethodConfigReducer,
  generalConfig: generalConfigReducer,

  // logisticsConfig: logisticsConfigReducer,
  logisticsConfig: logisticsDeliveryMethodsConfigReducer,
  // logisticsDeliveryMethodsConfig: logisticsDeliveryMethodsConfigReducer,
  logisticShippingConfig: logisticsShippingConfigReducer,
  logisticPickupConfig: logisticsPickupConfigReducer,
  logisticPromotionConfig: logisticsPromotionConfigReducer,

  legalConfig: legalConfigReducer,
  taxesConfig: taxesConfigReducer,
  accountsConfig: accountsConfigReducer,
  map: polygonReducer,
  referral: referralReducer,
  crm: crmReducer,
  subCategories: subcategoriesReducer,
  salesProcessConfig: salesProcessConfigReducer,
  store: storeReducer,
  information: informationReducer,
  ui: uiReducer,
  tiersList: tiersListReducer,
  onBoarding: onBoardingReducer,
  checkout: checkoutReducer,
  dashboard: dashboardReducer,
  reviews: reviewsReducer,
  coupons: couponsReducer,
  promotions: promotionsReducer,
  reports: reportsReducer,
  notifications: notificationsReducer,
  myStore: myStoreReducer,
  productPage: productPageReducer,
  customDomain: customDomainReducer,
  bulkEdit: bulkEditReducer,
  chat: chatReducer,
  chatTriggers: chatTriggerReducer,
  quickReply: quickReplyReducer,
  scheduleMessage: scheduleMessageReducer,
  multichatFunnel: multichatFunnelReducer,
  homePage: homePageReducer,
  productVisibility: productVisibilityReducer,
  taxInvoice: eInvoiceReducer,
  realTimeMultichat: realTimeMultichatReducer,
  blog: blogReducer,
  tierRestriction: tierRestrictionReducer,
  toastAlert: toastAlertReducer,
  productFilters: productFiltersReducer,
  pageLayout: pageLayoutReducer,
  productBatchLoad: productBatchLoadReducer,
});

export const rootEpic = combineEpics(
  // auth
  getSessionEpic,
  refreshSessionEpic,
  loginEpic,
  retryLoginEpic,
  sendEmailRecoveryEpic,
  resetPasswordEpic,
  getDomainChangeTokenEpic,
  // registerEpic,
  logoutEpic,
  // categories
  fetchCategoriesEpic,
  fetchCategoriesSearchEpic,
  createCategoryEpic,
  editCategoryEpic,
  deleteCategoryEpic,
  sortCategoriesEpic,
  // country
  fetchCountriesEpic,
  getStoreCountryEpic,
  saveStoreCountryEpic,
  // currency,
  fetchCurrencyEpic,
  fetchCurrenciesEpic,
  setCurrencyEpic,
  fetchGeolocationEpic,
  // products
  fetchProductEpic,
  fetchProductsEpic,
  fetchFeaturedProductsEpic,
  createProductEpic,
  saveProductSectionEpic,
  editProductSectionEpic,
  addProductImageEpic,
  editProductEpic,
  enableProductEpic,
  deleteProductEpic,
  saveProductVariantsEpic,
  editProductVariantEpic,
  exportProductsEpic,
  batchUpdateProductsEpic,
  batchCreateProductsEpic,
  fetchFbFeedTokenEpic,
  fetchProductsCatalogueEpic,
  fetchProductVariantOptionsEpic,
  addProductsCatalogueEpic,
  fetchProductMetadataSettingEpic,
  fetchProductSizeChartsEpic,
  fetchProductSpecificationsEpic,
  fetchRelatedProductsEpic,
  //organize-products
  fetchOrganizedProductsEpic,
  sortProductEpic,
  // orders
  fetchOrdersEpic,
  fetchOrderStockErrorsEpic,
  fetchOrderEpic,
  fetchOrderTaxInvoiceDataEpic,
  fetchOrdersCountEpic,
  fetchCollaboratorInfoEpic,
  fetchOrdersTaxInvoiceDataEpic,
  fetchPreOrdersEpic,
  fetchPreOrderEpic,
  updatePreOrderStateEpic,
  updateOrderCollaboratorEpic,
  // editOrderEpic,
  updateOrderStateEpic,
  // fetchOrderAvailableRevertStatesEpic,
  saveOrderCommentEpic,
  fetchOrderCommentsEpic,
  updateOrderCommentEpic,
  deleteOrderCommentEpic,
  // manual-order
  fetchManualOrderProductsEpic,
  fetchShoppingCartPricesEpic,
  fetchProductPriceEpic,
  fetchOrderSummaryEpic,
  createOrderEpic,
  editOrderEpic,
  createPreOrderEpic,
  editPreOrderEpic,
  searchCustomersEpic,
  // config::general
  saveGeneralSettingsEpic,
  saveGeneralInformationEpic,
  saveStoreProfileInformationEpic,
  saveGeneralContactEpic,
  // config::account
  saveAccountEpic,
  fetchSubscriptionSettingEpic,
  saveSubscriptionSettingEpic,
  changeAccountPasswordEpic,
  // config::social-medias,
  saveSocialMediasEpic,
  // config::payment-methods
  getPaymentMethodsEpic,
  addPaymentMethodEpic,
  deletePaymentMethodEpic,
  updatePaymentMethodEpic,
  getPaymentMethodEpic,
  updateMercadoPagoCheckoutProEpic,
  updateIziPayEpic,
  updateCulqiEpic,
  updateStripeEpic,
  updatePaypalEpic,
  updateTransbankEpic,
  updateNiubizPayLinkEpic,
  updateNiubizWebEpic,
  addLogoPaymentMethodEpic,

  // // config::logistics,
  // getShippingMethodEpic,
  // fetchOrderShippingMethodEpic,
  // getPickupMethodEpic,
  // saveShippingMethodEpic,
  // savePickupMethodEpic,
  // fetchFlatRateEpic,
  // saveFlatRateEpic,
  // editFlatRateEpic,
  // deleteFlatRateEpic,
  // saveAdminRateEpic,
  // deleteAdminRateEpic,
  // fetchAdminRateEpic,
  // editAdminRateEpic,
  // savePromotionEpic,

  // config::logisticsDeliveryMethods
  getLogisticDeliveryMethodsEpic,
  // config::logisticsShipping
  getLogisticShippingMethodEpic,
  updateEnabledShippingMethodsEpic,
  saveCountriesEpic,
  saveActiveTypeRateEpic,
  fetchFlatRateEpic,
  saveFlatRateEpic,
  editFlatRateEpic,
  deleteFlatRateEpic,
  saveShippingAdvancedEpic,
  fetchShippingAdvancedEpic,
  editShippingAdvancedEpic,
  deleteShippingAdvancedEpic,
  saveShippingStandardEpic,
  fetchShippingStandardEpic,
  editShippingStandardEpic,
  deleteShippingStandardEpic,
  updateAllowOrderOutsideDeliveryZoneEpic,
  // config::logisticsPromotion
  getLogisticPromotionEpic,
  saveLogisticPromotionEpic,
  editLogisticPromotionEpic,
  deleteLogisticPromotionEpic,
  // config::logisticsPickup
  getLogisticPickupRateEpic,
  getLogisticPickupMethodEpic,
  logisticSavePickupRateEpic,
  logisitcDeletePickupRateEpic,
  enabledPickupMethodEpic,
  logisticUpdatePickupRateEpic,

  // legal
  fetchStoreLegalSettingEpic,
  saveStoreLegalSettingEpic,
  createFaqEpic,
  fetchFaqEpic,
  deleteFaqEpic,
  editFaqEpic,
  fetchTermsEpic,
  createTermsEpic,
  fetchRefundsEpic,
  createRefundsEpic,
  fetchShippingsEpic,
  createShippingsEpic,
  fetchAboutUsConfigEpic,
  createAboutUsConfigEpic,
  // config::accounts
  getCollaboratorsEpic,
  getCollaboratorEpic,
  createCollaboratorEpic,
  updateCollaboratorEpic,
  //referral
  fetchStorePointEpic,
  fetchStoreReferredsEpic,
  fetchStoreReferredsInfoEpic,
  redeemStorePointsEpic,
  getRedeemedBenefitEpic,
  //crm
  fetchCustomersEpic,
  fetchCustomerEpic,
  fetchCustomerOrdersEpic,
  editCustomerEpic,
  fetchCustomerCategoriesEpic,
  createCustomerCategoryEpic,
  editCustomerCategoryEpic,
  deleteCustomerCategoryEpic,
  getCustomerMessageEpic,
  saveCustomerMessageEpic,
  //sub-categories
  fetchSubcategoriesEpic,
  createSubcategoryEpic,
  updateSubcategoryEpic,
  deleteSubcategoryEpic,
  sortSubcategoryEpic,
  // config: salesProcess
  getSalesProcessInformationEpic,
  getOrderStateMessagesEpic,
  updateOrderStateMessagesEpic,
  createCheckoutInputEpic,
  editCheckoutInputEpic,
  deleteCheckoutInputEpic,
  sortCheckoutInputEpic,
  getCheckoutInputEpic,
  // config: taxes
  fetchTaxesSettingEpic,
  createTaxEpic,
  fetchTaxesEpic,
  fetchTaxEpic,
  updateTaxEpic,
  fetchProductTaxesEpic,
  // store
  fetchStoreEpic,
  fetchStorePreviousTierEpic,
  fetchStoreMetadataEpic,
  // information
  fetchContactTypesEpic,
  fetchSocialMediaTypesEpic,
  fetchContactEpic,
  fetchSocialEpic,
  // ui
  fetchStoreInformationEpic,
  fetchStoreProfileInformationEpic,
  fetchResellerInformationEpic,
  saveFacebookIntegrationEpic,
  fetchFacebookIntegrationEpic,
  saveDowngradeInformationEpic,
  getStoreProductSettingEpic,
  saveStoreProductSettingEpic,
  // on-boarding
  setInitialConfigEpic,
  saveCanalInfEpic,
  validateDomainEpic,
  getStoreTypesEpic,
  saveOnboardingInfEpic,
  getSupportInformationEpic,
  getGoalInformationEpic,
  // checkout
  fetchPaymentProvidersEpic,
  deleteCardEpic,
  fetchCardEpic,
  fetchCardsEpic,
  fetchPurchaseSummaryEpic,
  performSubscribeEpic,
  freeTrialSubscribeEpic,
  payUInitializeEpic,
  stripeInitializeEpic,
  stripeUpdateEpic,
  stripeAddCardInitializeEpic,
  checkCardStateEpic,
  setCurrentCardEpic,
  // tiers List
  fetchSubscriptionInformationEpic,
  fetchSubscriptionPriceEpic,
  //dashboard
  fetchProductsCountEpic,
  fetchDataAnalyticsEpic,
  fetchPageViewsEpic,
  fetchSummaryProductsEpic,
  fetchSummaryOrdersEpic,
  fetchSummaryProductCategoriesEpic,
  fetchSummarySalesEpic,
  fetchSummaryOrderCustomersEpic,
  fetchSummaryLessStockProductsEpic,
  fetchFlagLessStockProductsEpic,
  fetchSummaryPromotionSalesEpic,
  fetchSummaryOrderAmountEpic,
  fetchSummaryAverageTicketEpic,
  fetchSummaryPageViewsEpic,
  completeFirstStepsEpic,
  domainExpositionEpic,
  fetchCompleteFirstStepsEpic,
  fetchDomainExpositionEpic,
  saveCompletedConfigEpic,
  fetchCompletedConfigEpic,
  //reviews
  fetchPendingReviewsEpic,
  fetchReviewsEpic,
  enableReviewEpic,
  fetchAverageEpic,
  sendEmailEpic,
  //notifications
  fetchNotificationsEpic,
  fetchUnseenNotificationsEpic,
  seenNotificationEpic,
  seenAllNotificationsEpic,
  //coupons
  fetchCouponsEpic,
  fetchCouponEpic,
  createCouponEpic,
  updateCouponEpic,
  //promotions
  fetchPromotionsEpic,
  fetchPromotionEpic,
  updatePromotionEpic,
  sortPromotionEpic,
  createPromotionEpic,
  //reports
  fetchGAIntegrationEpic,
  updateGAIntegrationEpic,
  //my-store
  saveStoreInformationEpic,
  //product-page
  saveSizeChartEpic,
  editSizeChartEpic,
  deleteSizeChartEpic,
  getProductPageSettingEpic,
  saveProductPageSettingEpic,
  // custom domain
  validateSslEpic,
  generateSslEpic,
  getCustomDomainEpic,
  deleteCustomDomainEpic,

  //bulk edit
  fetchBulkEditProductsEpic,
  changeProductImageEpic,
  editProductFieldEpic,
  editProductFieldBulkEpic,

  // chat
  fetchStoreDevicesEpic,
  getRavenAccountEpic,

  //chat triggers
  // fetchTriggersEpic
  //homepages
  getBannersEpic,
  getStoreInformationBlocksEpic,
  // product filters
  fetchProductFiltersSettingEpic,
  saveProductFiltersSettingEpic,
  fetchProductSearchSettingEpic,
  saveProductSearchSettingEpic,
  fetchVectorizeStatusEpic,

  // page layout
  fetchPageLayoutEpic
);

export const rootReducer = (state: RootState, action: Action) => {
  // Clear higher order state when logout
  if (action.type === Logout.type) {
    // But keep store (this is agnostic)
    const store = {...state.store};

    //$FlowFixMe
    state = {...defaultInitialState, store};
  }
  return appReducer(state, action);
};
